import React, { useState } from "react";
import { Card, Row, Col, Collapse } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ExpandableCard.style.css";

const ExpandableCard = ({
  title,
  content,
  image,
  video,
  aiContent,
  exampleContent,
  examplesContent,
  initiallyOpen = false,
  layout = "standard",
  verticalCenter = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  return (
    <Card className="expandable-card mb-3">
      <div
        className="card-header-custom d-flex justify-content-between align-items-center p-3"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          backgroundColor: "#fff",
          borderBottom: isOpen ? "1px solid #ddd" : "none",
        }}
      >
        <h4 className="m-0" style={{ fontWeight: "bold" }}>
          {title}
        </h4>
        <div className="icon-container">
          <i
            className={`bi ${isOpen ? "bi-dash" : "bi-plus"}`}
            style={{ fontSize: "32px", color: "#b80900" }}
          ></i>
        </div>
      </div>
      <Collapse in={isOpen}>
        <div>
          <Card.Body className="p-4">
            {layout === "three-column" ? (
              <>
                <Row className="align-items-center mb-4">
                  {/* Image Column */}
                  {image && (
                    <Col
                      md={6}
                      className="mb-3 mb-md-0 d-flex justify-content-center"
                    >
                      <img
                        src={image}
                        alt={title}
                        className="img-fluid rounded shadow-sm"
                        style={{
                          maxHeight: "500px",
                          maxWidth: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Col>
                  )}

                  {/* Text Column */}
                  <Col md={image ? 6 : 12}>
                    <div
                      className="card-text-content w-100"
                      style={{ textAlign: "justify", lineHeight: "1.6" }}
                    >
                      {children || (
                        <div dangerouslySetInnerHTML={{ __html: content }} />
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Video Column - Centered at the bottom */}
                {video && (
                  <Row className="justify-content-center">
                    <Col
                      md={8}
                      lg={6}
                      className="d-flex flex-column align-items-center justify-content-center"
                    >
                      <h6
                        className="text-uppercase text-muted mb-2 text-center"
                        style={{ letterSpacing: "1px", fontSize: "0.75rem" }}
                      >
                        Video zum Thema
                      </h6>
                      <video
                        src={video}
                        controls
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "8px",
                          border: "1px solid #eee",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          backgroundColor: "#000",
                        }}
                      >
                        Ihr Browser unterstützt das Video-Tag nicht.
                      </video>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <>
                {/* Single Row with Two Columns Layout */}
                <Row
                  className={`${
                    verticalCenter ? "align-items-center" : "align-items-start"
                  } mb-4`}
                >
                  {/* Left Column: Image and Video - Only render if content exists */}
                  {(image || video) && (
                    <Col
                      md={image ? 6 : 12}
                      className="mb-3 mb-md-0 d-flex flex-column align-items-center justify-content-start"
                    >
                      {image && (
                        <img
                          src={image}
                          alt={title}
                          className="img-fluid rounded shadow-sm mb-4"
                          style={{
                            maxHeight: "600px",
                            width: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}

                      {video && (
                        <div className="w-100">
                          {image && (
                            <h6
                              className="text-uppercase text-muted mb-2 text-center"
                              style={{
                                letterSpacing: "1px",
                                fontSize: "0.75rem",
                              }}
                            >
                              Video zum Thema
                            </h6>
                          )}
                          <video
                            src={video}
                            controls
                            style={{
                              width: "100%",
                              height: "auto",
                              borderRadius: "8px",
                              border: "1px solid #eee",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                              backgroundColor: "#000",
                            }}
                          >
                            Ihr Browser unterstützt das Video-Tag nicht.
                          </video>
                        </div>
                      )}
                    </Col>
                  )}

                  {/* Right Column: Text, AI, and Examples */}
                  <Col md={image ? 6 : 12}>
                    <div
                      className="card-text-content w-100 mb-4"
                      style={{ textAlign: "justify", lineHeight: "1.6" }}
                    >
                      {children || (
                        <div dangerouslySetInnerHTML={{ __html: content }} />
                      )}
                    </div>

                    {/* AI Section */}
                    {aiContent && (
                      <div
                        className="p-4 rounded shadow-sm mb-3"
                        style={{
                          backgroundColor: "#fff5f5",
                          borderLeft: "4px solid #b80900",
                        }}
                      >
                        <div className="d-flex align-items-center mb-3">
                          <i
                            className="bi bi-cpu me-2"
                            style={{ fontSize: "1.5rem", color: "#b80900" }}
                          ></i>
                          <h5 className="mb-0" style={{ color: "#b80900" }}>
                            Künstliche Intelligenz
                          </h5>
                        </div>
                        <div
                          dangerouslySetInnerHTML={{ __html: aiContent }}
                          style={{ textAlign: "justify" }}
                        />
                      </div>
                    )}

                    {/* Practical Examples Section */}
                    {(
                      examplesContent ||
                      (exampleContent && [exampleContent])
                    )?.map((example, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded shadow-sm mb-3"
                        style={{
                          backgroundColor: "#fff5f5",
                          borderLeft: "4px solid #b80900",
                        }}
                      >
                        <div className="d-flex align-items-center mb-3">
                          <i
                            className="bi bi-lightbulb me-2"
                            style={{ fontSize: "1.5rem", color: "#b80900" }}
                          ></i>
                          <h5 className="mb-0" style={{ color: "#b80900" }}>
                            Praxisbeispiel: {example.title}
                          </h5>
                        </div>
                        <div style={{ textAlign: "justify" }}>
                          {example.text}
                        </div>
                        {example.link && (
                          <div className="mt-3">
                            <a
                              href={example.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-danger"
                              style={{
                                borderColor: "#b80900",
                                color: "#b80900",
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = "#b80900";
                                e.target.style.color = "#fff";
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = "transparent";
                                e.target.style.color = "#b80900";
                              }}
                            >
                              Mehr erfahren{" "}
                              <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default ExpandableCard;
